import { useEffect } from 'react';

/**
 * Accessibility Hook for Lighthouse 100/100 Accessibility Score
 */
export const useAccessibility = () => {
  useEffect(() => {
    // Skip links for keyboard navigation
    const addSkipLinks = () => {
      if (document.querySelector('.skip-links')) return;
      
      const skipLinks = document.createElement('div');
      skipLinks.className = 'skip-links';
      skipLinks.innerHTML = `
        <a href="#main-content" class="skip-link">Skip to main content</a>
        <a href="#navigation" class="skip-link">Skip to navigation</a>
        <a href="#footer" class="skip-link">Skip to footer</a>
      `;
      
      document.body.insertBefore(skipLinks, document.body.firstChild);
    };

    // Focus management for modals and overlays
    const manageFocus = () => {
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      
      // Trap focus in modals
      document.addEventListener('keydown', (e) => {
        if (e.key !== 'Tab') return;
        
        const modal = document.querySelector('[role="dialog"]:not([hidden])');
        if (!modal) return;
        
        const focusable = modal.querySelectorAll(focusableElements);
        const firstFocusable = focusable[0];
        const lastFocusable = focusable[focusable.length - 1];
        
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            lastFocusable.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            firstFocusable.focus();
            e.preventDefault();
          }
        }
      });
    };

    // Keyboard navigation for custom components
    const addKeyboardSupport = () => {
      document.addEventListener('keydown', (e) => {
        // Arrow key navigation for sliders/carousels
        if (e.target.getAttribute('role') === 'tablist') {
          const tabs = Array.from(e.target.querySelectorAll('[role="tab"]'));
          const currentIndex = tabs.indexOf(document.activeElement);
          
          let nextIndex = currentIndex;
          
          switch (e.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              nextIndex = (currentIndex + 1) % tabs.length;
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
              break;
            case 'Home':
              nextIndex = 0;
              break;
            case 'End':
              nextIndex = tabs.length - 1;
              break;
            default:
              return;
          }
          
          e.preventDefault();
          tabs[nextIndex].focus();
          tabs[nextIndex].click();
        }
      });
    };

    // Color contrast validation (development only)
    const validateContrast = () => {
      if (process.env.NODE_ENV !== 'development') return;
      
      // This would integrate with a color contrast library
      console.log('🎨 Color contrast validation enabled');
    };

    // Reduced motion support
    const handleReducedMotion = () => {
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      
      const handleMotionPreference = (e) => {
        if (e.matches) {
          document.documentElement.classList.add('reduce-motion');
        } else {
          document.documentElement.classList.remove('reduce-motion');
        }
      };
      
      handleMotionPreference(mediaQuery);
      mediaQuery.addListener(handleMotionPreference);
    };

    // ARIA live regions for dynamic content
    const setupLiveRegions = () => {
      if (document.querySelector('#live-region')) return;
      
      const liveRegion = document.createElement('div');
      liveRegion.id = 'live-region';
      liveRegion.setAttribute('aria-live', 'polite');
      liveRegion.setAttribute('aria-atomic', 'false');
      liveRegion.className = 'sr-only';
      document.body.appendChild(liveRegion);
      
      // Status region for urgent updates
      const statusRegion = document.createElement('div');
      statusRegion.id = 'status-region';
      statusRegion.setAttribute('aria-live', 'assertive');
      statusRegion.setAttribute('aria-atomic', 'true');
      statusRegion.className = 'sr-only';
      document.body.appendChild(statusRegion);
    };

    // Form validation accessibility
    const enhanceFormAccessibility = () => {
      const forms = document.querySelectorAll('form');
      
      forms.forEach(form => {
        form.addEventListener('submit', (e) => {
          const invalidFields = form.querySelectorAll(':invalid');
          
          if (invalidFields.length > 0) {
            e.preventDefault();
            
            // Focus first invalid field
            invalidFields[0].focus();
            
            // Announce error to screen readers
            const errorMessage = `Form contains ${invalidFields.length} error${invalidFields.length > 1 ? 's' : ''}. Please correct and try again.`;
            announceToScreenReader(errorMessage, 'assertive');
          }
        });
      });
    };

    // Image alt text validation (development only)
    const validateImageAlt = () => {
      if (process.env.NODE_ENV !== 'development') return;
      
      const images = document.querySelectorAll('img');
      images.forEach((img, index) => {
        if (!img.alt && !img.getAttribute('aria-hidden')) {
          console.warn(`🖼️ Image ${index + 1} missing alt text:`, img);
        }
      });
    };

    // Initialize all accessibility features
    addSkipLinks();
    manageFocus();
    addKeyboardSupport();
    validateContrast();
    handleReducedMotion();
    setupLiveRegions();
    enhanceFormAccessibility();
    validateImageAlt();
    
    // Cleanup
    return () => {
      // Remove event listeners if needed
    };
  }, []);
};

/**
 * Announce messages to screen readers
 */
export const announceToScreenReader = (message, priority = 'polite') => {
  const regionId = priority === 'assertive' ? 'status-region' : 'live-region';
  const region = document.getElementById(regionId);
  
  if (region) {
    region.textContent = message;
    
    // Clear after announcement
    setTimeout(() => {
      region.textContent = '';
    }, 1000);
  }
};

/**
 * Focus management utilities
 */
export const focusUtils = {
  // Save and restore focus for modals
  saveFocus: () => {
    window.__previousActiveElement = document.activeElement;
  },
  
  restoreFocus: () => {
    if (window.__previousActiveElement && window.__previousActiveElement.focus) {
      window.__previousActiveElement.focus();
      window.__previousActiveElement = null;
    }
  },
  
  // Find first focusable element
  getFirstFocusable: (container) => {
    const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
    return container.querySelector(focusableElements);
  },
  
  // Check if element is visible and focusable
  isFocusable: (element) => {
    return element.offsetWidth > 0 && 
           element.offsetHeight > 0 && 
           !element.disabled && 
           element.tabIndex !== -1;
  }
};

/**
 * ARIA utilities
 */
export const ariaUtils = {
  // Generate unique IDs for ARIA relationships
  generateId: (prefix = 'aria') => {
    return `${prefix}-${Math.random().toString(36).substr(2, 9)}`;
  },
  
  // Set ARIA expanded state
  setExpanded: (element, expanded) => {
    element.setAttribute('aria-expanded', expanded.toString());
  },
  
  // Set ARIA pressed state for toggle buttons
  setPressed: (element, pressed) => {
    element.setAttribute('aria-pressed', pressed.toString());
  },
  
  // Set ARIA selected state
  setSelected: (element, selected) => {
    element.setAttribute('aria-selected', selected.toString());
  }
};

export default useAccessibility;